import { useContext } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Slider from '@mui/material/Slider';
import IconButton from '@mui/material/IconButton';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import AnimationIcon from '@mui/icons-material/Animation';
import SettingsIcon from '@mui/icons-material/Settings';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import ZoomOutOutlinedIcon from '@mui/icons-material/ZoomOutOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimations } from '../../contexts/AnimationsContext';
import { useZoom, ZOOM_LEVELS } from '../../contexts/ZoomContext';
import { LanguageContext } from '../../contexts/LanguageContext';
import { SupportedLanguage } from '../../i18n';
import { palette, typography, withOpacity } from '../../theme/colors';
import { useAnimationStyles } from '../../contexts/AnimationsContext';
import { slideUp, scaleIn } from '../../theme/animations';

export const SettingsPage = () => {
  const { mode, setMode } = useContext(ColorModeContext);
  const { animationsEnabled, setAnimationsEnabled } = useAnimations();
  const { zoomLevel, setZoomLevel, zoomIn, zoomOut, resetZoom } = useZoom();
  const { language, setLanguage, languageNames, supportedLanguages } = useContext(LanguageContext);
  const animationStyles = useAnimationStyles();

  const handleZoomChange = (_event: Event, newValue: number | number[]) => {
    setZoomLevel(newValue as number);
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLanguage(event.target.value as SupportedLanguage);
  };

  const zoomMarks = ZOOM_LEVELS.filter((_, i) => i % 2 === 0).map((level) => ({
    value: level.value,
    label: level.label,
  }));

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    border: palette.border,
    text: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    border: palette.light.border,
    text: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          mb: 4,
          ...animationStyles.apply(slideUp(0)),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.accent} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SettingsIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.text,
              }}
            >
              Settings
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: typography.fontFamily.body,
                color: colors.textMuted,
                mt: 0.5,
              }}
            >
              Customize your super admin experience
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Settings Cards */}
      <Stack spacing={3}>
        {/* Appearance Settings */}
        <Card
          sx={{
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            ...animationStyles.apply(scaleIn(100)),
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.text,
                  mb: 0.5,
                }}
              >
                Appearance
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: typography.fontFamily.body,
                  color: colors.textMuted,
                }}
              >
                Customize the look and feel of the interface
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: colors.border }} />

            {/* Theme Mode */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 2,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                backgroundColor: mode === 'dark' ? withOpacity(palette.primary, 0.05) : 'transparent',
                transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: withOpacity(palette.primary, 0.08),
                  borderColor: palette.primary,
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    backgroundColor: withOpacity(mode === 'dark' ? palette.primary : palette.warning, 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {mode === 'dark' ? (
                    <DarkModeIcon sx={{ color: palette.primary, fontSize: 20 }} />
                  ) : (
                    <LightModeIcon sx={{ color: palette.warning, fontSize: 20 }} />
                  )}
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily: typography.fontFamily.body,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text,
                      fontSize: typography.fontSize.base,
                    }}
                  >
                    Dark Mode
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: typography.fontFamily.body,
                      color: colors.textMuted,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    Currently: {mode === 'dark' ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  label={mode === 'dark' ? 'DARK' : 'LIGHT'}
                  size="small"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: '10px',
                    fontWeight: typography.fontWeight.semibold,
                    backgroundColor: withOpacity(mode === 'dark' ? palette.primary : palette.warning, 0.15),
                    color: mode === 'dark' ? palette.primary : palette.warning,
                    border: `1px solid ${withOpacity(mode === 'dark' ? palette.primary : palette.warning, 0.3)}`,
                  }}
                />
                <Switch
                  checked={mode === 'dark'}
                  onChange={() => setMode()}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: palette.primary,
                      '& + .MuiSwitch-track': {
                        backgroundColor: palette.primary,
                      },
                    },
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Motion Settings */}
        <Card
          sx={{
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            ...animationStyles.apply(scaleIn(200)),
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.text,
                  mb: 0.5,
                }}
              >
                Motion & Animations
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: typography.fontFamily.body,
                  color: colors.textMuted,
                }}
              >
                Control interface animations and transitions
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: colors.border }} />

            {/* Animations Toggle */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 2,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                backgroundColor: animationsEnabled ? withOpacity(palette.accent, 0.05) : 'transparent',
                transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: withOpacity(palette.accent, 0.08),
                  borderColor: palette.accent,
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    backgroundColor: withOpacity(palette.accent, 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AnimationIcon sx={{ color: palette.accent, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily: typography.fontFamily.body,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text,
                      fontSize: typography.fontSize.base,
                    }}
                  >
                    Enable Animations
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: typography.fontFamily.body,
                      color: colors.textMuted,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    Smooth transitions and motion effects
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  label={animationsEnabled ? 'ON' : 'OFF'}
                  size="small"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: '10px',
                    fontWeight: typography.fontWeight.semibold,
                    backgroundColor: withOpacity(animationsEnabled ? palette.success : palette.textMuted, 0.15),
                    color: animationsEnabled ? palette.success : palette.textMuted,
                    border: `1px solid ${withOpacity(animationsEnabled ? palette.success : palette.textMuted, 0.3)}`,
                  }}
                />
                <Switch
                  checked={animationsEnabled}
                  onChange={(e) => setAnimationsEnabled(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: palette.accent,
                      '& + .MuiSwitch-track': {
                        backgroundColor: palette.accent,
                      },
                    },
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Interface Scale */}
        <Card
          sx={{
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            ...animationStyles.apply(scaleIn(250)),
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.text,
                  mb: 0.5,
                }}
              >
                Interface Scale
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: typography.fontFamily.body,
                  color: colors.textMuted,
                }}
              >
                Adjust the zoom level of the entire interface
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: colors.border }} />

            {/* Zoom Control */}
            <Stack
              sx={{
                p: 2,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                backgroundColor: withOpacity(palette.info, 0.05),
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      backgroundColor: withOpacity(palette.info, 0.15),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ZoomInOutlinedIcon sx={{ color: palette.info, fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: typography.fontFamily.body,
                        fontWeight: typography.fontWeight.semibold,
                        color: colors.text,
                        fontSize: typography.fontSize.base,
                      }}
                    >
                      Interface Zoom
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: typography.fontFamily.body,
                        color: colors.textMuted,
                        fontSize: typography.fontSize.xs,
                      }}
                    >
                      Scale the entire interface
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton
                    onClick={zoomOut}
                    disabled={zoomLevel <= 0.8}
                    size="small"
                    sx={{
                      color: colors.text,
                      '&:hover': { backgroundColor: withOpacity(palette.info, 0.1) },
                      '&.Mui-disabled': { color: colors.textMuted },
                    }}
                  >
                    <ZoomOutOutlinedIcon fontSize="small" />
                  </IconButton>
                  <Chip
                    label={`${Math.round(zoomLevel * 100)}%`}
                    size="small"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontSize: '11px',
                      fontWeight: typography.fontWeight.semibold,
                      backgroundColor: withOpacity(palette.info, 0.15),
                      color: palette.info,
                      border: `1px solid ${withOpacity(palette.info, 0.3)}`,
                      minWidth: 52,
                    }}
                  />
                  <IconButton
                    onClick={zoomIn}
                    disabled={zoomLevel >= 1.2}
                    size="small"
                    sx={{
                      color: colors.text,
                      '&:hover': { backgroundColor: withOpacity(palette.info, 0.1) },
                      '&.Mui-disabled': { color: colors.textMuted },
                    }}
                  >
                    <ZoomInOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={resetZoom}
                    disabled={zoomLevel === 1.0}
                    size="small"
                    sx={{
                      color: colors.text,
                      '&:hover': { backgroundColor: withOpacity(palette.warning, 0.1) },
                      '&.Mui-disabled': { color: colors.textMuted },
                    }}
                  >
                    <RestartAltOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              <Box sx={{ px: 1 }}>
                <Slider
                  value={zoomLevel}
                  onChange={handleZoomChange}
                  min={0.8}
                  max={1.2}
                  step={0.05}
                  marks={zoomMarks}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                  sx={{
                    color: palette.info,
                    '& .MuiSlider-track': {
                      height: 6,
                    },
                    '& .MuiSlider-rail': {
                      height: 6,
                      backgroundColor: withOpacity(palette.info, 0.2),
                    },
                    '& .MuiSlider-thumb': {
                      width: 18,
                      height: 18,
                      backgroundColor: palette.info,
                      '&:hover, &.Mui-focusVisible': {
                        boxShadow: `0 0 0 8px ${withOpacity(palette.info, 0.16)}`,
                      },
                    },
                    '& .MuiSlider-mark': {
                      backgroundColor: withOpacity(palette.info, 0.4),
                      width: 2,
                      height: 8,
                    },
                    '& .MuiSlider-markActive': {
                      backgroundColor: palette.info,
                    },
                    '& .MuiSlider-markLabel': {
                      fontFamily: typography.fontFamily.mono,
                      fontSize: '10px',
                      color: colors.textMuted,
                    },
                    '& .MuiSlider-valueLabel': {
                      fontFamily: typography.fontFamily.mono,
                      fontSize: '11px',
                      backgroundColor: palette.info,
                    },
                  }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Language Preferences */}
        <Card
          sx={{
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            ...animationStyles.apply(scaleIn(300)),
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.text,
                  mb: 0.5,
                }}
              >
                Language Preferences
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: typography.fontFamily.body,
                  color: colors.textMuted,
                }}
              >
                Choose your preferred language for the interface
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: colors.border }} />

            <RadioGroup value={language} onChange={handleLanguageChange}>
              {supportedLanguages.map((lang) => (
                <Stack
                  key={lang}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => setLanguage(lang)}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: '12px',
                    border: `1px solid ${language === lang ? palette.success : colors.border}`,
                    backgroundColor: language === lang ? withOpacity(palette.success, 0.05) : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: withOpacity(palette.success, 0.08),
                      borderColor: palette.success,
                    },
                    '&:last-child': {
                      mb: 0,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        backgroundColor: withOpacity(palette.success, 0.15),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.semibold,
                          color: palette.success,
                          textTransform: 'uppercase',
                        }}
                      >
                        {lang.toUpperCase()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: typography.fontFamily.body,
                          fontWeight: typography.fontWeight.semibold,
                          color: colors.text,
                          fontSize: typography.fontSize.base,
                        }}
                      >
                        {languageNames[lang]}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: typography.fontFamily.body,
                          color: colors.textMuted,
                          fontSize: typography.fontSize.xs,
                        }}
                      >
                        {lang === 'en' && 'English - Left to right'}
                        {lang === 'fr' && 'French - Left to right'}
                        {lang === 'ar' && 'Arabic - Right to left'}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {language === lang && (
                      <Chip
                        label="ACTIVE"
                        size="small"
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontSize: '10px',
                          fontWeight: typography.fontWeight.semibold,
                          backgroundColor: withOpacity(palette.success, 0.15),
                          color: palette.success,
                          border: `1px solid ${withOpacity(palette.success, 0.3)}`,
                        }}
                      />
                    )}
                    <FormControlLabel
                      value={lang}
                      control={
                        <Radio
                          sx={{
                            color: colors.textMuted,
                            '&.Mui-checked': {
                              color: palette.success,
                            },
                          }}
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  </Stack>
                </Stack>
              ))}
            </RadioGroup>

            {/* Status indicator */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: '8px',
                backgroundColor: withOpacity(palette.success, 0.1),
                border: `1px solid ${withOpacity(palette.success, 0.3)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <TranslateOutlinedIcon sx={{ color: palette.success, fontSize: 18 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    color: palette.success,
                    fontWeight: typography.fontWeight.semibold,
                  }}
                >
                  {languageNames[language]} selected - Interface will update immediately
                </Typography>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {/* System Info */}
        <Card
          sx={{
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            ...animationStyles.apply(scaleIn(400)),
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.text,
                  mb: 0.5,
                }}
              >
                System Information
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: typography.fontFamily.body,
                  color: colors.textMuted,
                }}
              >
                Application details and version
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: colors.border }} />

            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    color: colors.textMuted,
                    fontSize: typography.fontSize.xs,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  Version
                </Typography>
                <Chip
                  label="v2.0.0"
                  size="small"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: '10px',
                    fontWeight: typography.fontWeight.semibold,
                    backgroundColor: withOpacity(palette.info, 0.15),
                    color: palette.info,
                    border: `1px solid ${withOpacity(palette.info, 0.3)}`,
                  }}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    color: colors.textMuted,
                    fontSize: typography.fontSize.xs,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  Design System
                </Typography>
                <Chip
                  label="MIDNIGHT AURORA"
                  size="small"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: '10px',
                    fontWeight: typography.fontWeight.semibold,
                    backgroundColor: withOpacity(palette.primary, 0.15),
                    color: palette.primary,
                    border: `1px solid ${withOpacity(palette.primary, 0.3)}`,
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};
